/**
 * src/App.js
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { ToastProvider }         from './context/ToastContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider }          from './context/CartContext';

import { Layout }        from './components/layout';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { NetworkBanner } from './components/common/ErrorBoundary';
import { PageLoader }    from './components/ui';
import LegalBanner       from './components/common/LegalBanner';
import WhatsAppButton    from './components/common/WhatsAppButton';

import HomePage          from './pages/HomePage';
import ProductsPage      from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage          from './pages/CartPage';
import CheckoutPage      from './pages/CheckoutPage';
import LoginPage         from './pages/LoginPage';
import RegisterPage      from './pages/RegisterPage';
import NotFoundPage      from './pages/NotFoundPage';
import AboutPage         from './pages/AboutPage';
import SafetyPage        from './pages/SafetyPage';
import ContactPage       from './pages/ContactPage';
import TrackOrderPage    from './pages/TrackOrderPage';

import AdminDashboard from './pages/admin/AdminDashboard';
import AdminProducts  from './pages/admin/AdminProducts';
import AdminOrders    from './pages/admin/AdminOrders';
import AdminCoupons   from './pages/admin/AdminCoupons';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [pathname]);
  return null;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  const location          = useLocation();
  if (loading)        return <PageLoader />;
  if (!user)          return <Navigate to="/login" state={{ from: location }} replace />;
  if (!user.is_staff) return <Navigate to="/"     replace />;
  return children;
}

function RedirectIfAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <PageLoader />;
  if (user)    return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        <Route element={<Layout />}>
          <Route index               element={<ErrorBoundary><HomePage /></ErrorBoundary>} />
          <Route path="products"     element={<ErrorBoundary><ProductsPage /></ErrorBoundary>} />
          <Route path="products/:slug" element={<ErrorBoundary><ProductDetailPage /></ErrorBoundary>} />
          <Route path="cart"         element={<ErrorBoundary><CartPage /></ErrorBoundary>} />
          <Route path="checkout"     element={<ErrorBoundary><CheckoutPage /></ErrorBoundary>} />
          <Route path="about"        element={<ErrorBoundary><AboutPage /></ErrorBoundary>} />
          <Route path="safety"       element={<ErrorBoundary><SafetyPage /></ErrorBoundary>} />
          <Route path="contact"      element={<ErrorBoundary><ContactPage /></ErrorBoundary>} />
          <Route path="track-order"  element={<ErrorBoundary><TrackOrderPage /></ErrorBoundary>} />
          <Route path="login"    element={<RedirectIfAuth><LoginPage /></RedirectIfAuth>} />
          <Route path="register" element={<RedirectIfAuth><RegisterPage /></RedirectIfAuth>} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="/admin"          element={<RequireAdmin><ErrorBoundary><AdminDashboard /></ErrorBoundary></RequireAdmin>} />
        <Route path="/admin/products" element={<RequireAdmin><ErrorBoundary><AdminProducts  /></ErrorBoundary></RequireAdmin>} />
        <Route path="/admin/orders"   element={<RequireAdmin><ErrorBoundary><AdminOrders   /></ErrorBoundary></RequireAdmin>} />
        <Route path="/admin/coupons"  element={<RequireAdmin><ErrorBoundary><AdminCoupons  /></ErrorBoundary></RequireAdmin>} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <NetworkBanner />
        <AuthProvider>
          <CartProvider>
            <AppRoutes />
            <LegalBanner />
            <WhatsAppButton />
          </CartProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
